import { useState } from "react";
import { Plus } from "lucide-react";
import { useStore, lineKey } from "@/context/StoreContext";
import { imgUrl, inr } from "@/lib/api";
import { StockBadge, QtyStepper } from "@/components/store/Bits";

const CutPicker = ({ p, cut, setCut }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`cut-picker-${p.id}`}>
    {[{ label: "", charge: 0 }, ...p.cut_options].map((o) => (
      <button key={o.label || "whole"} type="button" onClick={() => setCut(o.label)} data-testid={`cut-option-${p.id}-${(o.label || "whole").toLowerCase().replace(/\W+/g, "-")}`}
        className={`rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors ${cut === o.label ? "bg-primary text-white border-primary" : "bg-white text-primary border-stone-200 hover:border-primary/40"}`}>
        {o.label || "Whole"}{o.charge > 0 ? ` +${inr(o.charge)}` : ""}
      </button>
    ))}
  </div>
);

export const ProductCard = ({ p, index }) => {
  const { cart, add, setQty } = useStore();
  const [cut, setCut] = useState("");
  const key = lineKey(p.id, cut);
  const line = cart.find((i) => i.key === key);
  const soon = p.stock_status === "coming_soon";
  const out = p.stock_status === "unavailable" || soon;
  const charge = cut ? p.cut_options.find((o) => o.label === cut)?.charge || 0 : 0;
  const tid = cut ? `${p.id}-${cut.toLowerCase().replace(/\W+/g, "-")}` : p.id;
  return (
    <article data-testid={`product-card-${p.id}`} style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}
      className={`rise group bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-md transition-[transform,box-shadow] duration-300 ${out && !soon ? "opacity-70" : ""}`}>
      <div className="relative aspect-[4/3.4] overflow-hidden bg-stone-100">
        <img src={imgUrl(p.image)} alt={p.name} loading="lazy" className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${out && !soon ? "grayscale" : ""}`} />
        <div className="absolute top-3 left-3"><StockBadge status={p.stock_status} qty={p.available_qty} testId={`stock-badge-${p.id}`} /></div>
      </div>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="font-display text-xl sm:text-2xl leading-tight text-primary font-semibold">{p.name}</h3>
          <p className="text-xs text-stone-500 mt-0.5">{p.unit}</p>
          {p.includes?.length > 0 && <p className="text-xs text-stone-600 mt-1 line-clamp-2" data-testid={`product-includes-${p.id}`}>Includes: {p.includes.join(", ")}</p>}
          {!out && (p.needs_prep || cut) && <p className="text-[11px] font-bold text-secondary mt-1" data-testid={`product-prep-${p.id}`}>Cut fresh · next-day delivery</p>}
        </div>
        {!out && p.cut_options?.length > 0 && <CutPicker p={p} cut={cut} setCut={setCut} />}
        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="text-lg font-extrabold text-[#1A2E1C]" data-testid={`product-price-${p.id}`}>{soon ? <span className="text-sm text-stone-500 font-semibold">Price soon</span> : inr(p.price + charge)}</span>
          {out ? (
            <span className="text-xs text-stone-400 font-semibold" data-testid={`product-soon-${p.id}`}>{soon ? "Coming soon" : "Back soon"}</span>
          ) : line ? (
            <QtyStepper qty={line.qty} max={p.available_qty} onChange={(q) => setQty(key, q)} testId={`product-qty-${tid}`} />
          ) : (
            <button onClick={() => add(p, cut)} data-testid={`add-to-cart-${tid}`}
              className="h-10 px-4 rounded-full border-2 border-primary text-primary font-bold text-sm flex items-center gap-1 hover:bg-primary hover:text-white active:scale-95 transition-[transform,background-color,color]">
              <Plus className="w-4 h-4" /> Add
            </button>
          )}
        </div>
      </div>
    </article>
  );
};
